import React, { useCallback, useEffect, useState, FC } from 'react';
import { useLocation } from 'react-router-dom';
import classnames from 'classnames';
import { ChevronDown } from 'react-bootstrap-icons';
import { Paper } from '@mui/material';

import { reduxActions, useDispatch } from 'store';
import { Link } from 'components/controls';

import { useGetPanelNavigationDefinition } from 'plugins/api/endpoints/useGetPanelNavigationDefinition';
import { IDynamicCategory } from 'plugins/api/types';

import styles from 'theme/components/layouts/ManagmentPanelLayout/components/DashboardMenu.module.scss';

// typ danych wejściowych
interface IProps {
  isAdminPanel?: boolean;
}

const DashboardMenu: FC<IProps> = () => {
  const dispatch = useDispatch();

  // ścieżka z url
  const { pathname } = useLocation();

  const { data: menu } = useGetPanelNavigationDefinition();

  // lista (indeksów tablicy) rozwiniętych bloków w menu
  const [openedParentItemIndexes, setOpenedParentItemIndexes] = useState<number[]>([]);

  // funkcja testujacy czy podana ścieżka jest aktualną stroną
  const isCurrentPath = useCallback(
    (testedPath: string) => pathname.includes(testedPath),
    [pathname]
  );

  const isManagementPanelRoot = /\/managment_panel\/?$/.test(pathname);

  useEffect(() => {
    const blockItemIndex =
      menu?.navigation_definition.categories.findIndex((menuItem) =>
        menuItem.items?.some((subItem) =>
          isCurrentPath(subItem.component_name || subItem.dynamic_page)
        )
      ) ?? -1;

    if (blockItemIndex >= 0) {
      setOpenedParentItemIndexes((prevState) =>
        prevState.includes(blockItemIndex) ? prevState : [...prevState, blockItemIndex]
      );
    }
  }, [pathname, menu, isCurrentPath]);

  // rozwinięcie/zwinięcie bloku w mwnu
  const toggleItem = (index: number) => {
    setOpenedParentItemIndexes((prevState) =>
      openedParentItemIndexes.includes(index)
        ? prevState.filter((item) => item !== index)
        : [...prevState, index]
    );
  };

  const renderParentItems = (parentItem: IDynamicCategory, index: number) => {
    return (
      <div className={styles.menuItem} key={index}>
        {!parentItem.items ? (
          <Link
            to="/managment_panel"
            onClick={() => dispatch(reduxActions.setIsMobileMenu(false))}
            className={classnames(styles.link, {
              [styles.active]: isManagementPanelRoot
            })}>
            <>
              <div className={styles.iconWrapper}>
                <i className={`bi bi-${parentItem.icon}`}></i>
              </div>
              {parentItem.name}
            </>
          </Link>
        ) : (
          <div
            className={classnames(styles.label, {
              [styles.open]: openedParentItemIndexes.includes(index),
              [styles.labelActive]: (parentItem.items || []).some((subItem) =>
                isCurrentPath(subItem.component_name || subItem.dynamic_page)
              )
            })}
            onClick={() => toggleItem(index)}>
            <div className={styles.iconWrapper}>
              <i className={`bi bi-${parentItem.icon}`}></i>
            </div>
            {parentItem.name}
            <ChevronDown className={styles.arrow} />
          </div>
        )}

        {openedParentItemIndexes.includes(index) && (
          <div className={styles.subItemsWrapper}>
            {(parentItem.items || []).map((subItem, subItemIndex) => (
              <Link
                key={subItemIndex}
                to={
                  subItem.component_name
                    ? `/managment_panel/cms/${subItem.component_name}`
                    : `/managment_panel/dynamic_page/${subItem.dynamic_page}`
                }
                onClick={() => {
                  dispatch(reduxActions.setIsMobileMenu(false));
                }}
                className={classnames(styles.subItem, {
                  [styles.current]: isCurrentPath(subItem.component_name || subItem.dynamic_page)
                })}>
                {subItem.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Paper className={classnames(styles.componentWrapper, 'StylePath-Containers-DashboardMenu')}>
      <div className={styles.menuItems}>
        {menu?.navigation_definition.categories.map((parentItem, index) =>
          renderParentItems(parentItem, index)
        )}
        <div className={styles.menuItem}>
          <Link
            to="/dev-studio"
            onClick={() => dispatch(reduxActions.setIsMobileMenu(false))}
            className={classnames(styles.link)}>
            <>
              <div className={styles.iconWrapper}>
                <i className="bi bi-code-slash"></i>
              </div>
              Dev Studio
            </>
          </Link>
        </div>
      </div>
    </Paper>
  );
};

export default DashboardMenu;
